import { useState, useEffect, useCallback } from 'react';
import { smsApi } from '../services/api';
import { useGlobalToast } from '../contexts/ToastContext';

export interface SMSBatch {
  id: string;
  timeRange: {
    start: string;
    end: string;
    batchLevel: string;
  };
  summary: {
    totalMessages: number;
    successfulMessages: number;
    failedMessages: number;
    totalCost: number;
    successRate: number;
    averageDeliveryTime?: number;
  };
  percentiles?: {
    deliveryTime_50th: number;
    deliveryTime_90th: number;
    deliveryTime_95th: number;
    deliveryTime_99th: number;
  };
  messages: SMSNotification[];
}

export interface SMSNotification {
  id: string;
  type: string;
  recipient: string;
  recipientName: string;
  template: string;
  data: Record<string, unknown>;
  priority: string;
  status: string;
  createdAt: string;
  sentAt?: string;
  error?: string;
  provider?: string;
  cost?: number;
}

interface BatchedHistoryResponse {
  success: boolean;
  data: {
    batches: SMSBatch[];
    totalBatches: number;
    totalMessages: number;
    overallStats: {
      successRate: number;
      totalCost: number;
      averageDeliveryTime?: number;
      percentiles?: {
        deliveryTime_50th: number;
        deliveryTime_90th: number;
        deliveryTime_95th: number;
        deliveryTime_99th: number;
      };
    };
  };
}

interface SMSHistoryState {
  batches: SMSBatch[];
  totalBatches: number;
  totalMessages: number;
  overallStats: {
    successRate: number;
    totalCost: number;
    averageDeliveryTime?: number;
    percentiles?: {
      deliveryTime_50th: number;
      deliveryTime_90th: number;
      deliveryTime_95th: number;
      deliveryTime_99th: number;
    };
  };
  isLoading: boolean;
  hasData: boolean;
}

interface UseSMSHistoryReturn extends SMSHistoryState {
  // Actions
  refreshHistory: () => Promise<void>;
  loadHistory: (filters?: Record<string, unknown>) => Promise<void>;
}

interface UseSMSHistoryProps {
  selectedTownId?: string;
  dateRange?: { start: string; end: string };
  limit?: number;
}

export const useSMSHistory = ({
  selectedTownId,
  dateRange,
  limit = 50
}: UseSMSHistoryProps = {}): UseSMSHistoryReturn => {
  const { error } = useGlobalToast();

  // History state
  const [historyState, setHistoryState] = useState<SMSHistoryState>({
    batches: [],
    totalBatches: 0,
    totalMessages: 0,
    overallStats: {
      successRate: 0,
      totalCost: 0
    },
    isLoading: false,
    hasData: false
  });

  // Load SMS history
  const loadHistory = useCallback(async (filters?: Record<string, unknown>): Promise<void> => {
    setHistoryState(prev => ({ ...prev, isLoading: true }));
    console.log('[useSMSHistory] Loading SMS history...', { selectedTownId, dateRange, limit, filters });

    try {
      const response = await smsApi.getBatchedHistory({
        townId: selectedTownId,
        startDate: dateRange?.start,
        endDate: dateRange?.end,
        limit,
        ...filters
      });

      if (response.success && response.data) {
        const data = response.data as BatchedHistoryResponse['data'];
        console.log('[useSMSHistory] History loaded:', data);

        setHistoryState({
          batches: data.batches || [],
          totalBatches: data.totalBatches || 0,
          totalMessages: data.totalMessages || 0,
          overallStats: data.overallStats || {
            successRate: 0,
            totalCost: 0
          },
          isLoading: false,
          hasData: (data.batches?.length || 0) > 0
        });
      } else {
        console.log('[useSMSHistory] No history data available');
        setHistoryState(prev => ({
          ...prev,
          batches: [],
          totalBatches: 0,
          totalMessages: 0,
          overallStats: {
            successRate: 0,
            totalCost: 0
          },
          isLoading: false,
          hasData: false
        }));
      }
    } catch (err) {
      console.error('[useSMSHistory] Error loading history:', err);
      error('Failed to load SMS history');
      setHistoryState(prev => ({
        ...prev,
        batches: [],
        totalBatches: 0,
        totalMessages: 0,
        overallStats: {
          successRate: 0,
          totalCost: 0
        },
        isLoading: false,
        hasData: false
      }));
    }
  }, [selectedTownId, dateRange, limit, error]);

  const refreshHistory = async (): Promise<void> => {
    await loadHistory();
  };

  // Effects
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return {
    ...historyState,
    refreshHistory,
    loadHistory,
  };
};
